import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../app/session";
import { useMembers } from "../app/lists";
import { useChat } from "../app/chat";
import { usePlaces, type PlaceActions } from "../app/places";
import type { PlaceView } from "../app/views";
import { usePaneFill } from "../app/pane";
import {
  closeDraft,
  minimiseDraft,
  openDraft,
  patchDraft,
  useDraftValues,
} from "../app/draft";
import { PANE_HEIGHT } from "../app/chrome";
import { AddButton, Button, Card, TextField } from "../components/ui";
import { Sheet } from "../components/Sheet";
import { ScreenHeader } from "../components/ScreenHeader";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { AttachmentStrip } from "../components/AttachmentStrip";
import { useMediaUrl } from "../lib/media";
import {
  distanceMetres,
  formatDistance,
  nearby,
  parseCoordinates,
  directionsUrl,
  type Point,
} from "../lib/geo";
import { cn } from "../lib/cn";
import { useT } from "../i18n/index.js";
import { api } from "../lib/api";
import type { GeocodeResult } from "@vmeste/shared";
import {
  ChatIcon,
  CheckIcon,
  ClipboardIcon,
  EditIcon,
  LocateIcon,
  MapPinIcon,
  RouteIcon,
  StarIcon,
  TrashIcon,
} from "../components/icons";

/**
 * Места — the map (SPEC §4.10).
 *
 * Two views of one set of rows: a map, because a place is somewhere, and a
 * list, because a phone in a pocket is not a map. The screen owns neither the
 * geometry (`lib/geo.ts`) nor the tiles (`lib/map.ts`) nor the writes
 * (`app/places.tsx`) — it decides what is on screen and what a tap means.
 *
 * **The map is loaded when the map is looked at.** MapLibre is a quarter of a
 * megabyte compressed, which is more than the rest of this app put together,
 * and three of the four screens never draw one. The list works while it loads
 * and works if it never does.
 *
 * **Where the phone is never leaves the phone.** «Рядом» is worked out here,
 * from rows already synced, and nothing about a position is written down or
 * sent anywhere. The spec is blunt about why the promise cannot go further
 * than this screen being open: no browser reports a position in the background.
 */

const MapCanvas = lazy(() =>
  import("../components/MapCanvas")
    .then((module) => ({ default: module.MapCanvas }))
    /*
     * A chunk that will not load is a normal outcome here, not a crash.
     *
     * It is deliberately left out of the precache (see `vite.config.ts`), so
     * opening this screen with no network is exactly the case that fails —
     * and an unhandled rejection inside `lazy` takes down the whole page,
     * list and all. This turns it into the sentence the screen already knows
     * how to say.
     */
    .catch(() => ({ default: MapUnavailable })),
);

function MapUnavailable() {
  const t = useT();

  return (
    <div className="bg-surface-alt text-muted grid h-full place-items-center rounded-card px-6 text-center text-sm">
      {t("places.mapMissing")}
    </div>
  );
}

/** «Рядом» means this close. A kilometre is a walk, not a journey. */
const NEARBY_RADIUS_M = 1_000;

type View = "map" | "list";

/**
 * Why the phone did not say where it is.
 *
 * Five answers rather than one, because they call for four different things:
 * turn the permission back on, turn the receiver on, wait, or nothing at all.
 * «Не удалось определить, где вы» said all four at once, which is how a
 * message becomes something to shrug at.
 */
type LocationTrouble = "denied" | "blocked" | "unavailable" | "slow" | "unsupported";

const LOCATION_TROUBLE: Record<
  LocationTrouble,
  | "places.locationDenied"
  | "places.locationBlocked"
  | "places.locationUnavailable"
  | "places.locationSlow"
  | "places.locationUnsupported"
> = {
  denied: "places.locationDenied",
  blocked: "places.locationBlocked",
  unavailable: "places.locationUnavailable",
  slow: "places.locationSlow",
  unsupported: "places.locationUnsupported",
};

/**
 * A place being written, which is not the same thing as a place.
 *
 * `NewPlace` demands coordinates, because a place without them cannot be put
 * anywhere. A half-written one is allowed to have none yet — that is what
 * being half-written means — so the draft carries a nullable point and
 * `placeReady` is the moment the two shapes meet.
 */
export interface PlaceDraft {
  title: string;
  address: string;
  note: string;
  url: string;
  visited: boolean;
  point: Point | null;
}

const EMPTY_PLACE: PlaceDraft = {
  title: "",
  address: "",
  note: "",
  url: "",
  visited: false,
  point: null,
};

/** Which form this is, for the draft that outlives the screen. */
const PLACE_DRAFT = "place.new";

function placeReady(values: PlaceDraft): boolean {
  return values.title.trim().length > 0 && values.point !== null;
}

/** Been, not been, or everything. */
type Filter = "all" | "todo" | "visited";

/**
 * The lowest score a place may have and still be shown.
 *
 * Nought is «any», and the rest are the only thresholds worth having: below
 * three is «не понравилось», and nobody filters a list down to what they
 * disliked. One chip that cycles rather than four, because four chips for one
 * question is a row that has to be read.
 */
const RATING_STEPS = [0, 3, 4, 5] as const;
type MinRating = (typeof RATING_STEPS)[number];

const FILTER_LABEL: Record<Filter, "places.filterAll" | "places.filterTodo" | "places.filterVisited"> = {
  all: "places.filterAll",
  todo: "places.filterTodo",
  visited: "places.filterVisited",
};

export function MapPage() {
  const t = useT();
  const { session } = useSession();
  const spaceId = session?.spaceId ?? null;
  const self = session?.user.id ?? null;
  const places = usePlaces(spaceId, self);
  const members = useMembers(spaceId);
  const chat = useChat(spaceId, self, null);
  const navigate = useNavigate();

  const [view, setView] = useState<View>("map");
  /*
   * One piece of state for «which place», not two.
   *
   * The map used to select a pin and raise a strip along the bottom carrying
   * the same name again, and tapping that strip moved to the list, where the
   * row had to be tapped a third time to see anything. Three taps and two
   * copies of one name to answer «what is this». Selecting a pin *is* opening
   * it, and the sheet is the one place a place is looked at.
   */
  const [openId, setOpenId] = useState<string | null>(null);
  /*
   * The form's state is not this screen's.
   *
   * It lives in `app/draft` so that pushing the sheet down and walking off to
   * the calendar keeps every word of it. What is left here is the question
   * «is my form the one open right now», and the answer is a read.
   */
  const adding = useDraftValues<PlaceDraft>(PLACE_DRAFT);

  const startPlace = useCallback(
    (at?: Point) => {
      openDraft<PlaceDraft>({
        key: PLACE_DRAFT,
        path: "/map",
        title: t("places.add"),
        values: { ...EMPTY_PLACE, ...(at ? { point: at } : {}) },
        isReady: placeReady,
        submit: (input) => {
          if (!input.point) return;
          places.add({
            title: input.title,
            lat: input.point.lat,
            lng: input.point.lng,
            address: input.address,
            note: input.note,
            url: input.url,
            visited: input.visited,
          });
        },
      });
    },
    // `places.add` is rebuilt whenever the space changes, and the closure
    // stored in the draft has to be the current one — it may be called long
    // after this screen is gone.
    [places, t],
  );
  const [filter, setFilter] = useState<Filter>("all");
  /*
   * «Ближе всего» rather than «последнее добавленное».
   *
   * A separate switch from the filters because it is a different question, and
   * because it costs something the others do not: it needs the phone to say
   * where it is. Off until somebody asks for it.
   */
  const [byDistance, setByDistance] = useState(false);
  const [minRating, setMinRating] = useState<MinRating>(0);
  const [me, setMe] = useState<Point | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<LocationTrouble | null>(null);

  const screenRef = useRef<HTMLDivElement>(null);
  const fill = usePaneFill(screenRef);

  const open = places.places.find((place) => place.id === openId) ?? null;

  /**
   * Ask the phone where it is, once, when the screen opens.
   *
   * Once and not continuously: a watch is a battery cost for a screen somebody
   * looks at for ten seconds, and the answer at the moment of opening is what
   * «рядом» is about. A refusal is a normal answer and leaves the map alone.
   */
  const locate = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError("unsupported");
      return;
    }

    setLocating(true);
    setLocationError(null);

    const arrived = (position: GeolocationPosition) => {
      setMe({ lat: position.coords.latitude, lng: position.coords.longitude });
      setLocating(false);
    };

    /*
     * Two attempts, and they ask for different things.
     *
     * The first is cheap: a cached fix if there is one, no radio. That answers
     * instantly indoors and on a laptop with a recent fix — and fails outright
     * on a phone that has not been asked in a while, which is most of them.
     * The second turns the receiver on and waits, which is what somebody
     * pressing «где я» actually meant.
     *
     * Refusal is not retried: asking a second time after «нет» is what makes
     * people turn a permission off for good.
     */
    const askAgain = (trouble: GeolocationPositionError) => {
      if (trouble.code === trouble.PERMISSION_DENIED) {
        setLocating(false);
        /*
         * «Denied» arrives from two very different places.
         *
         * One is the person: they were asked and said no, and the answer is to
         * change that in the site's settings. The other is the site itself —
         * a `Permissions-Policy` header that turns the feature off for the
         * whole document, in which case nothing the person does in any
         * settings screen will help, because they were never asked. The
         * Permissions API can tell the two apart: a state of «granted» beside
         * a refusal is the second one.
         *
         * That was not hypothetical: this app shipped with `geolocation=()`
         * in its own headers, and told everybody to check their permissions.
         */
        void navigator.permissions
          ?.query({ name: "geolocation" as PermissionName })
          .then((permission) => {
            setLocationError(permission.state === "granted" ? "blocked" : "denied");
          })
          .catch(() => setLocationError("denied"));
        return;
      }

      navigator.geolocation.getCurrentPosition(arrived, (second) => {
        setLocating(false);
        setLocationError(
          second.code === second.PERMISSION_DENIED
            ? "denied"
            : second.code === second.TIMEOUT
              ? "slow"
              : "unavailable",
        );
      }, { enableHighAccuracy: true, timeout: 25_000, maximumAge: 0 });
    };

    navigator.geolocation.getCurrentPosition(arrived, askAgain, {
      enableHighAccuracy: false,
      timeout: 6_000,
      maximumAge: 120_000,
    });
  }, []);

  /*
   * Asked once, when the screen opens.
   *
   * It used to wait to be asked, which kept the promise narrow but left every
   * row without the one fact that makes a list of places actionable — how far
   * it is. A refusal is a normal answer and the screen carries on without
   * distances; nothing is stored and nothing is sent (SPEC §4.10).
   */
  useEffect(locate, [locate]);

  const near = useMemo(
    () => (me ? nearby(places.places, me, NEARBY_RADIUS_M) : []),
    [me, places.places],
  );

  const listed = useMemo(() => {
    const kept = places.places
      .filter((place) =>
        filter === "all" ? true : filter === "visited" ? place.visited : !place.visited,
      )
      // A place nobody has rated is not «below four», it is unanswered — and
      // an unanswered place has no business in a list of what was good.
      .filter((place) => minRating === 0 || (place.rating ?? 0) >= minRating);

    const withDistance = kept.map((place) => ({
      ...place,
      distance: me && place.point ? distanceMetres(me, place.point) : null,
    }));

    if (!byDistance || !me) return withDistance;

    // A place this device cannot locate sinks to the bottom rather than
    // dropping out: it is still on the list, it is simply not anywhere.
    return withDistance.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
  }, [me, places.places, filter, byDistance, minRating]);

  function discuss(place: PlaceView) {
    const existing = chat.discussionFor({ type: "place", id: place.id });
    const id = existing?.id ?? chat.createThread(place.title, { type: "place", id: place.id });
    if (id) navigate(`/chat?topic=${id}`);
  }

  return (
    <div ref={screenRef} className="flex flex-col" style={{ height: fill ?? PANE_HEIGHT }}>
      <div className="px-4 pt-4">
        <ScreenHeader
          title={t("places.title")}
          subtitle={t("places.subtitle")}
          action={
            <AddButton
              dimmed={adding !== null}
              label={t("places.add")}
              onClick={() => startPlace()}
            />
          }
        >
          <ViewSwitch view={view} onChange={setView} />
        </ScreenHeader>
      </div>

      {/* Only over the map. In the list the same question is answered better by
          the list itself — «ближе всего» sorts every row and puts the distance
          on each one — and a band of chips above it would be the same places
          said twice. */}
      {view === "map" && near.length > 0 ? (
        <NearbyBar
          places={near}
          onOpen={(id) => {
            setOpenId(id);
            setView("map");
          }}
        />
      ) : null}

      {view === "map" ? (
        <div className="relative min-h-0 flex-1 px-4 pb-4">
          <Suspense
            fallback={
              <div className="bg-surface-alt text-muted grid h-full place-items-center rounded-card text-sm">
                {t("places.mapLoading")}
              </div>
            }
          >
            <MapCanvas
              className="h-full overflow-hidden rounded-card"
              places={places.places}
              selectedId={openId}
              onSelect={setOpenId}
              me={me}
              centre={me}
              spaceId={spaceId}
              onPickPoint={(point) =>
                startPlace(point)
              }
            />
          </Suspense>

          {/* Top right, because the bottom right is where MapLibre keeps the
              attribution control. */}
          <div className="pointer-events-none absolute top-3 right-7 flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={locate}
              aria-label={t("places.locate")}
              className="bg-surface text-ink pointer-events-auto grid size-10 place-items-center rounded-full shadow-soft ring-1 ring-line"
            >
              <LocateIcon
                aria-hidden="true"
                className={cn("size-5", locating && "animate-pulse")}
              />
            </button>

            {locationError ? (
              <p className="bg-surface text-muted pointer-events-auto max-w-[13rem] rounded-row px-3 py-1.5 text-xs shadow-soft">
                {t(LOCATION_TROUBLE[locationError])}
              </p>
            ) : null}
          </div>

        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <Filters
            filter={filter}
            onFilter={setFilter}
            minRating={minRating}
            onMinRating={setMinRating}
            byDistance={byDistance}
            locating={locating}
            onByDistance={(next) => {
              setByDistance(next);
              if (next && !me) locate();
            }}
          />
          <PlaceList
            places={listed}
            spaceId={spaceId}
            onOpen={setOpenId}
            empty={filter === "all" ? "places.empty" : "places.emptyFiltered"}
          />
        </div>
      )}

      {/* Outside both views, because a place is opened from either of them and
          it is the same sheet either way. */}
      {open ? (
        <PlaceSheet
          place={open}
          spaceId={spaceId}
          self={self}
          members={members}
          actions={places}
          distance={me && open.point ? distanceMetres(me, open.point) : null}
          discussionCount={
            chat.discussionFor({ type: "place", id: open.id })?.messageCount ?? 0
          }
          onDiscuss={() => discuss(open)}
          onClose={() => setOpenId(null)}
        />
      ) : null}

      {adding ? (
        <AddPlaceSheet
          values={adding}
          onChange={patchDraft}
          me={me}
          onLocate={locate}
          onClose={closeDraft}
          onMinimise={minimiseDraft}
          onAdd={(input) => {
            if (!input.point) return;
            closeDraft();
            const id = places.add({
              title: input.title,
              lat: input.point.lat,
              lng: input.point.lng,
              address: input.address,
              note: input.note,
              url: input.url,
              visited: input.visited,
            });
            if (id) {
              setOpenId(id);
              setView("map");
            }
          }}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ pieces */

/**
 * Been, not been, or everything — and «ближе всего» beside it.
 *
 * The first pair is what somebody actually wants from a list of places: «куда
 * мы собирались» is a different list from «где нам понравилось», and one
 * screen carrying both without a way to say which is a screen you have to read
 * line by line. Chips rather than a menu, because there are three of them and
 * the answer should cost one tap.
 */
function Filters({
  filter,
  onFilter,
  minRating,
  onMinRating,
  byDistance,
  locating,
  onByDistance,
}: {
  filter: Filter;
  onFilter(next: Filter): void;
  minRating: MinRating;
  onMinRating(next: MinRating): void;
  byDistance: boolean;
  locating: boolean;
  onByDistance(next: boolean): void;
}) {
  const t = useT();

  const nextRating = () =>
    onMinRating(RATING_STEPS[(RATING_STEPS.indexOf(minRating) + 1) % RATING_STEPS.length]!);

  return (
    <div className="flex shrink-0 items-center gap-2 overflow-x-auto px-4 pb-3">
      {(["all", "todo", "visited"] as const).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={filter === option}
          onClick={() => onFilter(option)}
          className={cn(
            "shrink-0 rounded-full px-3 py-1.5 text-xs transition-colors",
            filter === option
              ? "bg-accent-soft text-accent-ink"
              : "bg-surface-alt text-muted",
          )}
        >
          {t(FILTER_LABEL[option])}
        </button>
      ))}

      <button
        type="button"
        aria-pressed={minRating > 0}
        onClick={nextRating}
        className={cn(
          "flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs transition-colors",
          minRating > 0 ? "bg-accent-soft text-accent-ink" : "bg-surface-alt text-muted",
        )}
      >
        <StarIcon
          aria-hidden="true"
          className="size-3.5"
          fill={minRating > 0 ? "currentColor" : "none"}
        />
        {minRating === 0
          ? t("places.filterRated")
          : minRating === 5
            ? "5"
            : `${minRating}+`}
      </button>

      <span className="bg-line-soft h-5 w-px shrink-0" aria-hidden="true" />

      <button
        type="button"
        aria-pressed={byDistance}
        onClick={() => onByDistance(!byDistance)}
        className={cn(
          "flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs transition-colors",
          byDistance ? "bg-accent-soft text-accent-ink" : "bg-surface-alt text-muted",
        )}
      >
        <LocateIcon
          aria-hidden="true"
          className={cn("size-3.5", locating && "animate-pulse")}
        />
        {t("places.byDistance")}
      </button>
    </div>
  );
}

function ViewSwitch({ view, onChange }: { view: View; onChange(next: View): void }) {
  const t = useT();

  return (
    <div className="bg-surface-alt flex shrink-0 rounded-full p-0.5" role="tablist">
      {(["map", "list"] as const).map((option) => (
        <button
          key={option}
          type="button"
          role="tab"
          aria-selected={view === option}
          onClick={() => onChange(option)}
          className={cn(
            "rounded-full px-3 py-1.5 text-xs transition-colors",
            view === option ? "bg-surface text-ink shadow-soft" : "text-muted",
          )}
        >
          {t(option === "map" ? "places.viewMap" : "places.viewList")}
        </button>
      ))}
    </div>
  );
}

/**
 * «Рядом с вами» — the whole of what this app can honestly promise about
 * proximity, and it says so underneath.
 */
function NearbyBar({
  places,
  onOpen,
}: {
  places: Array<PlaceView & { distance: number }>;
  onOpen(placeId: string): void;
}) {
  const t = useT();

  return (
    <section className="shrink-0 px-4 pb-3">
      <h2 className="text-muted mb-1 text-xs">
        {places.length === 1 ? t("places.nearbyOne") : t("places.nearby")}
      </h2>
      <ul className="flex gap-2 overflow-x-auto pb-1">
        {places.map((place) => (
          <li key={place.id}>
            <button
              type="button"
              onClick={() => onOpen(place.id)}
              className="bg-surface text-ink flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ring-1 ring-line"
            >
              <span className="max-w-[9rem] truncate">{place.title}</span>
              <span className="text-muted text-xs whitespace-nowrap">
                <Distance metres={place.distance} />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Distance({ metres }: { metres: number }) {
  const t = useT();
  const { value, unit } = formatDistance(metres);

  return <>{t(unit === "m" ? "places.metres" : "places.kilometres", { count: value })}</>;
}

function Rating({ place }: { place: PlaceView }) {
  const t = useT();

  if (place.rating === null) {
    return <>{place.visited ? t("places.visited") : t("places.notVisited")}</>;
  }

  const shown = Number.isInteger(place.rating)
    ? String(place.rating)
    : place.rating.toFixed(1);

  return (
    <>
      {"★ "}
      {shown}
      {place.reviews.length > 1 ? ` · ${t("places.averageOf", { count: place.reviews.length })}` : ""}
    </>
  );
}

/* -------------------------------------------------------------------- list */

interface Listed extends PlaceView {
  distance: number | null;
}

function PlaceList({
  places,
  spaceId,
  onOpen,
  empty,
}: {
  places: Listed[];
  spaceId: string | null;
  onOpen(placeId: string): void;
  /** What an empty list says depends on why it is empty. */
  empty: "places.empty" | "places.emptyFiltered";
}) {
  const t = useT();

  if (places.length === 0) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        <p className="text-muted py-8 text-center text-sm">{t(empty)}</p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
      <ul className="flex flex-col gap-2">
        {places.map((place) => (
          <li key={place.id}>
            <PlaceRow place={place} spaceId={spaceId} onOpen={() => onOpen(place.id)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlaceRow({
  place,
  spaceId,
  onOpen,
}: {
  place: Listed;
  spaceId: string | null;
  onOpen(): void;
}) {
  const photo = useMediaUrl(spaceId, place.photo ?? "");

  return (
    <Card className="p-0">
      <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 p-3 text-left">
        {photo ? (
          <img src={photo} alt="" className="size-14 shrink-0 rounded-row object-cover" />
        ) : (
          <span className="bg-surface-alt text-muted grid size-14 shrink-0 place-items-center rounded-row">
            <MapPinIcon aria-hidden="true" className="size-5" />
          </span>
        )}

        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{place.title}</span>
          {place.address ? (
            <span className="text-muted block truncate text-sm">{place.address}</span>
          ) : null}
          <span className="text-muted block truncate text-sm">
            <Rating place={place} />
            {place.distance !== null ? (
              <>
                {" · "}
                <Distance metres={place.distance} />
              </>
            ) : null}
          </span>
        </span>
      </button>
    </Card>
  );
}

/* ------------------------------------------------------------------- sheet */

/**
 * Everything one place carries.
 *
 * The same shape a wish and an event already have: a card answers what it is,
 * and the sheet a tap away holds the words, the pictures, the opinions and the
 * ways of acting on it.
 */
function PlaceSheet({
  place,
  spaceId,
  self,
  members,
  actions,
  distance,
  discussionCount,
  onDiscuss,
  onClose,
}: {
  place: PlaceView;
  spaceId: string | null;
  self: string | null;
  members: Array<{ user: { id: string; displayName: string } }>;
  actions: PlaceActions;
  /** How far away, when the phone has been asked and answered. */
  distance: number | null;
  discussionCount: number;
  onDiscuss(): void;
  onClose(): void;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const nameOf = (userId: string) =>
    members.find((member) => member.user.id === userId)?.user.displayName ??
    t("chat.someone");

  const mine = self ? place.reviews.find((review) => review.userId === self) : undefined;
  const others = place.reviews.filter((review) => review.userId !== self);

  return (
    <>
      <Sheet title={place.title} onClose={onClose}>
        {/* The sheet gives its body no padding — every caller supplies its
            own, so that a strip of photographs can run to the edge while text
            does not. */}
        <div className="flex flex-col gap-4 px-4 py-4">
          {editing ? (
            <EditPlaceForm
              place={place}
              onCancel={() => setEditing(false)}
              onSave={(patch) => {
                actions.edit(place.id, patch);
                setEditing(false);
              }}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    "rounded-full px-2.5 py-1 text-xs",
                    place.visited
                      ? "bg-accent-soft text-accent-ink"
                      : "bg-surface-alt text-muted",
                  )}
                >
                  <Rating place={place} />
                </span>
                {distance !== null ? (
                  <span className="text-muted text-xs">
                    <Distance metres={distance} />
                  </span>
                ) : null}
                <span className="text-muted text-xs">
                  {t("places.addedBy", { name: nameOf(place.createdBy) })}
                </span>
              </div>

              {place.address ? <p className="text-sm">{place.address}</p> : null}
              {place.note ? <p className="text-ink-soft text-sm whitespace-pre-wrap">{place.note}</p> : null}
              {place.url ? (
                <a
                  href={place.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-accent truncate text-sm underline"
                >
                  {place.url}
                </a>
              ) : null}

              <div className="flex flex-wrap gap-2">
                {place.point ? (
                  <a
                    href={directionsUrl(place.point, place.title, navigator.platform ?? "")}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="bg-surface-alt text-ink inline-flex min-h-[var(--touch)] items-center justify-center gap-2 rounded-pill px-5 text-[0.9375rem] font-semibold"
                  >
                    <RouteIcon aria-hidden="true" className="size-4" />
                    {t("places.route")}
                  </a>
                ) : null}

                <Button variant="secondary" onClick={onDiscuss}>
                  <ChatIcon aria-hidden="true" className="size-4" />
                  {t("places.discuss")}
                  {discussionCount > 0 ? ` · ${discussionCount}` : ""}
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => actions.setVisited(place.id, !place.visited)}
                >
                  {place.visited ? t("places.markNotVisited") : t("places.markVisited")}
                </Button>

              </div>

              <p className="text-muted text-xs">{t("places.routeHint")}</p>

              {/* Directly under the actions, and not at the bottom of the
                  sheet, because adding one is an action too — taken standing
                  in the place, one-handed. It is the only way in: a second
                  «добавить фотографию» button among the actions above was two
                  doors into one room. */}
              <div>
                <h3 className="text-muted mb-2 text-xs">{t("places.photos")}</h3>
                <AttachmentStrip
                  spaceId={spaceId}
                  target={{ type: "place", id: place.id }}
                />
              </div>

              <ReviewForm
                place={place}
                mine={mine}
                onSave={(rating, text) => actions.review(place.id, rating, text)}
                onRemove={() => actions.unreview(place.id)}
              />

              {others.length > 0 ? (
                <ul className="flex flex-col gap-2">
                  {others.map((review) => (
                    <li key={review.userId} className="bg-surface-alt rounded-row p-3">
                      <p className="text-sm font-medium">
                        {nameOf(review.userId)} · ★ {review.rating}
                      </p>
                      {review.text ? (
                        <p className="text-ink-soft mt-1 text-sm whitespace-pre-wrap">
                          {review.text}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  <EditIcon aria-hidden="true" className="size-4" />
                  {t("places.editPlace")}
                </Button>
                <Button variant="danger" onClick={() => setConfirming(true)}>
                  <TrashIcon aria-hidden="true" className="size-4" />
                  {t("common.delete")}
                </Button>
              </div>
            </>
          )}
        </div>
      </Sheet>

      {confirming ? (
        <ConfirmDialog
          title={place.title}
          confirmLabel={t("places.removePlace")}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            actions.remove(place.id);
            setConfirming(false);
            onClose();
          }}
        >
          {t("places.removeConfirm")}
        </ConfirmDialog>
      ) : null}
    </>
  );
}

/**
 * A score out of five, and words if there are any.
 *
 * Stars rather than a number field: five taps is the whole interaction, and a
 * number field on a phone opens a keyboard for one digit.
 */
function ReviewForm({
  place,
  mine,
  onSave,
  onRemove,
}: {
  place: PlaceView;
  mine: PlaceView["reviews"][number] | undefined;
  onSave(rating: number, text?: string): void;
  onRemove(): void;
}) {
  const t = useT();
  const [rating, setRating] = useState(mine?.rating ?? 0);
  const [text, setText] = useState(mine?.text ?? "");

  // A correction arriving from the other device while this is open should show,
  // and an unsaved edit in progress should not be thrown away by it.
  const savedRef = useRef(mine);
  useEffect(() => {
    if (savedRef.current === mine) return;
    savedRef.current = mine;
    setRating(mine?.rating ?? 0);
    setText(mine?.text ?? "");
  }, [mine]);

  const dirty = rating !== (mine?.rating ?? 0) || text !== (mine?.text ?? "");

  return (
    <section className="bg-surface-alt flex flex-col gap-2 rounded-row p-3">
      <h3 className="text-muted text-xs">{t("places.yourRating")}</h3>

      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((score) => (
          <button
            key={score}
            type="button"
            aria-label={t("places.rateStars", { count: score })}
            aria-pressed={rating >= score}
            onClick={() => setRating(score)}
            className={cn("p-1", rating >= score ? "text-accent" : "text-muted")}
          >
            <StarIcon
              aria-hidden="true"
              className="size-5"
              fill={rating >= score ? "currentColor" : "none"}
            />
          </button>
        ))}

        {mine ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-muted ml-auto text-xs underline"
          >
            {t("places.removeReview")}
          </button>
        ) : null}
      </div>

      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={2}
        placeholder={t("places.reviewPlaceholder")}
        aria-label={t("places.reviewLabel")}
        className="bg-surface text-ink w-full resize-none rounded-row px-3 py-2 text-sm ring-1 ring-line"
      />

      {rating > 0 && dirty ? (
        <Button onClick={() => onSave(rating, text)}>{t("places.saveReview")}</Button>
      ) : null}

      {place.reviews.length === 0 ? (
        <p className="text-muted text-xs">{t("places.noReviews")}</p>
      ) : null}
    </section>
  );
}

/**
 * Editing a place: what it is and where it is, and nothing else.
 *
 * No score, no review, no route. Those are not properties of the place — a
 * rating is one person's opinion of it and a route is something you do with
 * it — and a form that offered them would be a form that could not be
 * cancelled cleanly.
 *
 * The coordinates are a field, in the plainest form there is. Somewhere put
 * down by a long press is roughly right by definition, and «roughly» is a
 * street away; a pair of numbers can be corrected from another map, which is
 * where they came from in the first place.
 */
function EditPlaceForm({
  place,
  onCancel,
  onSave,
}: {
  place: PlaceView;
  onCancel(): void;
  onSave(patch: {
    title: string;
    address: string;
    note: string;
    url: string;
    lat?: number;
    lng?: number;
  }): void;
}) {
  const t = useT();
  const [title, setTitle] = useState(place.title);
  const [address, setAddress] = useState(place.address ?? "");
  const [note, setNote] = useState(place.note ?? "");
  const [url, setUrl] = useState(place.url ?? "");
  const [point, setPoint] = useState(
    place.point ? `${place.point.lat}, ${place.point.lng}` : "",
  );

  const parsed = parseCoordinates(point);
  const pointBroken = point.trim().length > 0 && parsed === null;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!title.trim() || pointBroken) return;
        onSave({
          title,
          address,
          note,
          url,
          ...(parsed ? { lat: parsed.lat, lng: parsed.lng } : {}),
        });
      }}
    >
      <TextField
        label={t("places.titleLabel")}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        required
      />
      <TextField
        label={t("places.addressLabel")}
        value={address}
        onChange={(event) => setAddress(event.target.value)}
      />
      <TextField
        label={t("places.coordinatesLabel")}
        value={point}
        inputMode="decimal"
        className="tabular"
        onChange={(event) => setPoint(event.target.value)}
      />
      {pointBroken ? (
        <p className="text-neg text-xs">{t("places.coordinatesBroken")}</p>
      ) : null}
      <TextField
        label={t("places.urlLabel")}
        value={url}
        inputMode="url"
        onChange={(event) => setUrl(event.target.value)}
      />
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted text-xs">{t("places.noteLabel")}</span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          placeholder={t("places.notePlaceholder")}
          className="bg-surface text-ink w-full resize-none rounded-row px-3 py-2 text-sm ring-1 ring-line"
        />
      </label>

      <div className="flex gap-2">
        <Button type="submit" disabled={pointBroken}>
          {t("common.save")}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

/* --------------------------------------------------------------- adding */

/**
 * Putting a place on the map.
 *
 * Three ways in, because the three are how it actually happens: somebody is
 * looking at the map and presses where they mean; somebody has a link open in
 * another app; somebody knows the name and not the location. The sheet holds
 * all three and commits the same row whichever was used.
 *
 * The point is required and the name is required; everything else is not. A
 * place with no coordinates would be a list item, and this screen already has
 * a list.
 */
function AddPlaceSheet({
  values,
  onChange,
  me,
  onLocate,
  onClose,
  onMinimise,
  onAdd,
}: {
  /**
   * Everything typed so far, held outside this component.
   *
   * Outside because the sheet can be pushed down and the screen behind it left
   * altogether — see `app/draft`. A form that kept its own state would keep it
   * exactly as long as the tab stayed open.
   */
  values: PlaceDraft;
  onChange(next: PlaceDraft): void;
  me: Point | null;
  onLocate(): void;
  onClose(): void;
  onMinimise(): void;
  onAdd(input: PlaceDraft): void;
}) {
  const t = useT();
  const { title, address, note, url, visited, point } = values;
  const set = (patch: Partial<PlaceDraft>) => onChange({ ...values, ...patch });

  const [linkError, setLinkError] = useState(false);

  /** A link from another map app: the point comes out of it, not out of a fetch. */
  function takeLink(text: string) {
    const parsed = parseCoordinates(text);
    setLinkError(text.trim().length > 0 && parsed === null);
    set({ url: text, ...(parsed ? { point: parsed } : {}) });
  }

  const ready = placeReady(values);

  return (
    <Sheet title={t("places.add")} onClose={onClose} onMinimise={onMinimise}>
      <form
        className="flex flex-col gap-3 px-4 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready) return;
          onAdd(values);
        }}
      >
        <PlaceSearch
          onPick={(result) => {
            setLinkError(false);
            set({
              // What somebody typed wins over what the search returned: they
              // called it «кофейня у дома», not «Кофейня №4».
              title: title || result.title,
              address: address || result.address || "",
              point: { lat: result.lat, lng: result.lng },
            });
          }}
        />

        <TextField
          label={t("places.titleLabel")}
          placeholder={t("places.titlePlaceholder")}
          value={title}
          onChange={(event) => set({ title: event.target.value })}
          required
        />

        <div className="flex items-end gap-2">
          {/* The wrapper is what grows: `TextField` passes `className` to the
              input, which is already full-width inside a column of its own. */}
          <div className="min-w-0 flex-1">
            <TextField
              label={t("places.urlLabel")}
              placeholder={t("places.pasteHint")}
              value={url}
              inputMode="url"
              onChange={(event) => takeLink(event.target.value)}
            />
          </div>
          <PasteButton onPaste={takeLink} />
        </div>

        {linkError ? (
          <p className="text-neg text-xs">{t("places.linkHasNoPoint")}</p>
        ) : null}

        <div className="bg-surface-alt flex items-center gap-2 rounded-row px-3 py-2 text-sm">
          <MapPinIcon aria-hidden="true" className="text-muted size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate tabular">
            {point
              ? `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`
              : t("places.dropPinHint")}
          </span>
          <button
            type="button"
            onClick={() => {
              if (me) set({ point: me });
              else onLocate();
            }}
            className="text-accent shrink-0 text-xs underline"
          >
            <LocateIcon aria-hidden="true" className="mr-1 inline size-3.5" />
            {t("places.useMyLocation")}
          </button>
        </div>

        <TextField
          label={t("places.addressLabel")}
          placeholder={t("places.addressPlaceholder")}
          value={address}
          onChange={(event) => set({ address: event.target.value })}
        />

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted text-xs">{t("places.noteLabel")}</span>
          <textarea
            value={note}
            onChange={(event) => set({ note: event.target.value })}
            rows={3}
            placeholder={t("places.notePlaceholder")}
            className="bg-surface text-ink w-full resize-none rounded-row px-3 py-2 text-sm ring-1 ring-line"
          />
        </label>

        {/* The list's own switch, not the browser's.

            A native checkbox draws whatever the platform draws — a blue tick
            and a square — and the app has exactly one shape for «отмечено»:
            round, empty as a ring, filled as the accent carrying a tick. It is
            in `ListsPage` too, and the day it becomes a component is the day a
            third screen needs it. */}
        <button
          type="button"
          role="checkbox"
          aria-checked={visited}
          onClick={() => set({ visited: !visited })}
          className="flex items-center gap-2 self-start py-1 text-sm"
        >
          <span
            className={cn(
              "grid size-[22px] shrink-0 place-items-center rounded-pill transition-colors",
              visited ? "bg-accent text-ground" : "ring-line text-transparent ring-1",
            )}
          >
            <CheckIcon aria-hidden="true" className="size-[14px]" strokeWidth={2.4} />
          </span>
          {t("places.visited")}
        </button>

        <Button type="submit" disabled={!ready} full>
          {t("common.add")}
        </Button>
      </form>
    </Sheet>
  );
}

/**
 * The search box, and the one honest sentence under it.
 *
 * Everything else in this app stays on our own machines; this does not, and a
 * person deciding whether to type a place name into it has a right to know
 * that before they type rather than in a settings screen afterwards.
 */
function PlaceSearch({ onPick }: { onPick(result: GeocodeResult): void }) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GeocodeResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);

  function run() {
    const text = query.trim();
    if (text.length < 2) return;

    setSearching(true);
    setFailed(false);
    api
      .geocode(text)
      .then((answer) => setResults(answer.results))
      .catch(() => setFailed(true))
      .finally(() => setSearching(false));
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <TextField
            label={t("places.search")}
            placeholder={t("places.searchPlaceholder")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              // Enter inside a form would submit it, and the form is the place
              // itself — which is not what pressing enter in a search box means.
              if (event.key !== "Enter") return;
              event.preventDefault();
              run();
            }}
          />
        </div>
        {/* «Найти», not «Найти место» — the label above the field says which
            thing is being looked for, and a button that repeats it is a
            second reading of one line. */}
        <Button type="button" variant="secondary" onClick={run} disabled={searching}>
          {searching ? t("common.loading") : t("places.searchAction")}
        </Button>
      </div>

      <p className="text-muted text-xs">{t("places.searchLeaves")}</p>

      {failed ? <p className="text-neg text-xs">{t("places.searchFailed")}</p> : null}

      {results !== null && results.length === 0 && !failed ? (
        <p className="text-muted text-xs">{t("places.searchEmpty")}</p>
      ) : null}

      {results && results.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {results.map((result) => (
            <li key={`${result.lat},${result.lng},${result.title}`}>
              <button
                type="button"
                onClick={() => {
                  onPick(result);
                  setResults(null);
                }}
                className="bg-surface-alt hover:bg-line w-full rounded-row px-3 py-2 text-left text-sm"
              >
                <span className="block font-medium">{result.title}</span>
                {result.address ? (
                  <span className="text-muted block truncate text-xs">{result.address}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** The clipboard, when the browser allows it. Silent when it does not. */
function PasteButton({ onPaste }: { onPaste(text: string): void }) {
  const t = useT();
  const [failed, setFailed] = useState(false);

  if (failed || typeof navigator === "undefined" || !navigator.clipboard?.readText) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard
          .readText()
          .then((text) => {
            const trimmed = text.trim();
            if (trimmed) onPaste(trimmed);
          })
          // Refused, or a browser that asks and was answered no. Once is enough
          // to know the button is not going to work here.
          .catch(() => setFailed(true));
      }}
      // No bottom margin: the row aligns on its ends, and the field beside
      // this one carries no hint under it to clear.
      className="bg-surface-alt text-ink-soft grid size-[var(--touch)] shrink-0 place-items-center rounded-pill"
      aria-label={t("places.pasteLink")}
      title={t("places.pasteLink")}
    >
      <ClipboardIcon className="size-[19px]" strokeWidth={1.8} />
    </button>
  );
}
